/**
 * Audio event types only — safe for `import type` from Game.ts.
 * No runtime side effects. Do not import AudioEngine / index from Game.
 */

export type LayerIdAudio = "mind" | "heart" | "will";

/** Pure karaoke clock — implemented by VoicePlayer adapter / linear default. */
export type ProclaimKaraokeDriver = {
  /**
   * Return word index in [0, wordCount] (wordCount means fully spoken).
   * Called from Game.update while proclaimLock > 0.
   * lockElapsed = lockMax - lockRemaining (seconds into speech).
   */
  wordIndex(lockElapsed: number, lockMax: number, wordCount: number): number;
};

export type AudioEvent =
  | { type: "ui.click" }
  | { type: "ui.illegal" }
  | { type: "ui.queue"; reference: string }
  | { type: "ui.queueCancel" }
  | { type: "mission.start" }
  | { type: "mission.won"; nearMiss?: boolean }
  | { type: "mission.lost" }
  | {
      type: "proclaim.start";
      verseId: string;
      category: string;
      speakSeconds: number;
      ref: string;
    }
  | { type: "proclaim.end"; verseId: string }
  | {
      type: "answer.resolve";
      verseId: string;
      steps: number;
      meet: boolean;
      correctTarget: boolean;
      lieType?: string;
    }
  | { type: "instant.layer"; verseId: string; selector: string; layer: string }
  | {
      type: "instant.still";
      verseId: string;
      layer: string;
      lieType?: string;
      duration: number;
      /** true when this Still also ran answerStepCount (may pair with answer.resolve) */
      withAnswer: boolean;
    }
  | { type: "instant.self"; verseId: string }
  | { type: "placeable.abide"; verseId: string; layer: string }
  | { type: "redeem"; verseId: string; lieType?: string; level?: number }
  | {
      type: "lie.spawn";
      lieId: string;
      lieType: string;
      level: number;
      layer: string;
      multiplied?: boolean;
      /**
       * Claim VO key (`vo/lies/{voId}-{level}`). Campaign skins set this so
       * spoken text matches the themed claim; falls back to lieId.
       */
      voId?: string;
    }
  | {
      /** Intensity step-down (III→II / II→I). VO only if channel free. */
      type: "lie.degrade";
      lieId: string;
      lieType: string;
      level: number;
      layer: string;
      voId?: string;
    }
  | { type: "lie.silence"; lieId: string; lieType: string; layer: string }
  | { type: "lie.breach"; layer: string }
  | { type: "lie.press"; layer: string }
  | { type: "overflow.flash"; layer: string }
  | { type: "reveal.open" }
  | { type: "reveal.close" }
  | { type: "pause"; paused: boolean };

/** Linear karaoke clock matching current Game formula. */
export function linearKaraokeDriver(): ProclaimKaraokeDriver {
  return {
    wordIndex(lockElapsed, lockMax, wordCount) {
      if (wordCount <= 0) return 0;
      if (lockMax <= 0) return wordCount;
      const t = Math.max(0, Math.min(1, lockElapsed / lockMax));
      return Math.min(wordCount, Math.floor(t * wordCount));
    },
  };
}
